import { ITokenBuy, ITokenBuyTableColumns, TOKEN_BUY_STATUSES } from '@entities/trades/token-buy';
import { ITableActionsConfig, TableActionBtns } from '@features/table-action-btns';
import { FilterControlBlock, FilterDateRange, FilterInput, FilterSelectMulti } from '@features/table-filter';
import { getSelectOptions } from '@shared/components/Form';
import RequestStatusLabel from '@shared/components/RequestStatusLabel';

import { ApproveTokenBuy } from '../components/ApproveTokenBuy';
import { RejectTokenBuy } from '../components/RejectTokenBuy';
import { TokenBuysToolbar } from '../components/TokenBuysToolbar';
import { WalletAddressLink } from '../components/WalletAddressLink';

interface IProps {
  refetch: () => void;
  refreshInterval?: string;
  setRefreshInterval?: (interval: string) => void;
}

export const useTableSettings = ({ refetch, refreshInterval, setRefreshInterval }: IProps) => {
  // THead settings
  const columnTitles: ITokenBuyTableColumns = {
    trader_id: 'Trader ID',
    amount: 'Amount',
    usdt_amount: 'USDT Amount',
    status: 'Status',
    wallet_address: 'Wallet Address (TRC)',
    created_at: 'Created At',
    updated_at: 'Updated At',
  };

  // filter
  const filterStatuses = [...TOKEN_BUY_STATUSES];
  const renderFilterElement = (key: keyof ITokenBuy) => {
    switch (key) {
      case 'status':
        return <FilterSelectMulti name={key} options={getSelectOptions(filterStatuses)} />;

      case 'created_at':
      case 'updated_at':
        return <FilterDateRange name={key} />;

      default:
        return <FilterInput name={key} />;
    }
  };

  const getTheadAfter = () => (
    <th>
      <FilterControlBlock storageKey='token-buys'>
        <TokenBuysToolbar
          refetch={refetch}
          refreshInterval={refreshInterval}
          setRefreshInterval={setRefreshInterval}
        />
      </FilterControlBlock>
    </th>
  );

  // TBody settings
  const renderRow = (key: keyof ITokenBuyTableColumns, tokenBuy: ITokenBuy) => {
    switch (key) {
      case 'status':
        return <RequestStatusLabel status={String(tokenBuy[key])} />;

      case 'wallet_address':
        return <WalletAddressLink address={tokenBuy[key]} />;

      default:
        return tokenBuy[key];
    }
  };

  const getTbodyAfter = (tokenBuy: ITokenBuy) => {
    const isEnabled = tokenBuy.status === 'pending' || tokenBuy.status === 'new';

    const actionBtnsConfig: ITableActionsConfig = {
      approve: {
        isEnabled,
        isVisible: true,
        popupElem: <ApproveTokenBuy tokenBuy={tokenBuy} refetch={refetch} />,
        popupSize: 'sm',
        allowedRoles: ['admin', 'operator'],
      },
      reject: {
        isEnabled,
        isVisible: true,
        popupElem: <RejectTokenBuy tokenBuy={tokenBuy} refetch={refetch} />,
        popupSize: 'sm',
        allowedRoles: ['admin', 'operator'],
      },
    };

    return (
      <td className='action-btns-row'>
        <TableActionBtns config={actionBtnsConfig} />
      </td>
    );
  };

  return {
    columnTitles,
    renderRow,
    renderFilterElement,
    getTheadAfter,
    getTbodyAfter,
  };
};
